import { memo, useCallback, useEffect, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link } from "@tanstack/react-router"
import {
	IconChevronDownOutline18 as ChevronDown,
	IconChevronLeftOutline18 as ChevronLeft,
	IconChevronRightOutline18 as ChevronRight,
	IconChevronUpOutline18 as ChevronUp,
} from "nucleo-ui-outline-18"

import type {
	AgentExampleAction,
	AgentRecommendationEvidence,
} from "@productlane/lib/src/agent-recommendations"
import { AI_CHAT_ACTOR_NAME } from "@productlane/lib/src/ai-chat-sync"
import { cn } from "@productlane/lib/src/browser"
import { Avatar, Button, IconButton } from "@productlane/ui"
import { StripeActionApprovalCard } from "@productlane/ui/src/components/ai-chat"
import { queries } from "@productlane/zero/queries"

import { AgentAvatar } from "./agent-message-header"
import {
	exampleActionPreview,
	IDEA_EXAMPLES_COPY,
	stepExample,
} from "./idea-examples-model"

const BUBBLE =
	"whitespace-pre-line rounded-[4px_12px_12px_12px] px-3 py-2 text-[13px] leading-relaxed"
const NOOP = () => undefined
const PLACEHOLDER_WIDTHS = ["w-full", "w-11/12", "w-2/3"]

// A long reply is a wall of text; three lines show the gist.
const ClampedBubble = memo(
	({
		testId,
		className,
		children,
	}: {
		testId: string
		className: string
		children: string
	}) => {
		const [expanded, setExpanded] = useState(false)
		const [clamped, setClamped] = useState(false)
		const measure = useCallback((node: HTMLParagraphElement | null) => {
			if (node) setClamped(node.scrollHeight > node.clientHeight + 1)
		}, [])
		return (
			<div className="flex flex-col items-start gap-1">
				<div data-testid={testId} className={cn(BUBBLE, className)}>
					{/* Clamped inside the padding, or a fourth line shows in it. */}
					<p ref={measure} className={cn(!expanded && "line-clamp-3")}>
						{children}
					</p>
				</div>
				{(clamped || expanded) && (
					<Button
						variant="link"
						size="sm"
						data-testid={`${testId}-more`}
						className="ml-3 font-medium"
						Icon={expanded ? ChevronUp : ChevronDown}
						onClick={() => setExpanded((value) => !value)}
					>
						{expanded ? "Show less" : "Show more"}
					</Button>
				)}
			</div>
		)
	},
)

const ReplySkeleton = memo(({ testId }: { testId: string }) => (
	<div
		data-testid={testId}
		aria-busy="true"
		className={cn(
			BUBBLE,
			"flex flex-col gap-2 bg-gray-100 py-3 dark:bg-gray-800",
		)}
	>
		{PLACEHOLDER_WIDTHS.map((width) => (
			<span
				key={width}
				className={cn(
					"h-2.5 animate-pulse rounded-full bg-gray-200 motion-reduce:animate-none dark:bg-gray-700",
					width,
				)}
			/>
		))}
	</div>
))

// Every example's thread stays synced and its avatar fetched, so stepping
// through examples swaps the name and avatar in the same frame as the text.
const ExampleWarmup = memo(({ threadId }: { threadId: string }) => {
	const [thread] = useQuery(queries.threadByID(threadId))
	const imageUrl = thread?.contact?.imageUrl
	useEffect(() => {
		if (imageUrl) new Image().src = imageUrl
	}, [imageUrl])
	return null
})

// One real conversation at a time: what the customer asked and how it went.
export const IdeaExamples = memo(
	({
		evidence,
		loadingThreadIds = [],
		engineering,
		exampleAction,
	}: {
		evidence: readonly AgentRecommendationEvidence[]
		// Examples whose answers the server is still writing.
		loadingThreadIds?: readonly string[]
		engineering: boolean
		exampleAction?: AgentExampleAction
	}) => {
		const [index, setIndex] = useState(0)
		const total = evidence.length
		const current = Math.min(index, Math.max(total - 1, 0))
		const item = evidence[current]
		const [thread] = useQuery(queries.threadByID(item?.threadId ?? ""), {
			enabled: !!item,
		})
		if (!item) return null
		const loading = loadingThreadIds.includes(item.threadId)
		const contact = thread?.contact
		const customer =
			contact?.name?.trim() ||
			contact?.email?.trim() ||
			IDEA_EXAMPLES_COPY.customer
		const step = (delta: number) =>
			setIndex((i) => stepExample(i, delta, total))

		return (
			<section
				data-testid="agent-idea-examples"
				className="flex flex-col gap-3"
			>
				{evidence.map((e) => (
					<ExampleWarmup key={e.threadId} threadId={e.threadId} />
				))}
				<div className="flex items-center justify-between gap-2">
					<span className="text-xs text-tertiary">
						{IDEA_EXAMPLES_COPY.position(current, total)}
					</span>
					<div className="flex items-center gap-1">
						<Link
							to="/threads/$id"
							params={{ id: item.threadId }}
							className="mr-1 text-xs text-tertiary hover:text-secondary"
						>
							{IDEA_EXAMPLES_COPY.openChat}
						</Link>
						{total > 1 && (
							<>
								<IconButton
									Icon={ChevronLeft}
									variant="ghost"
									aria-label={IDEA_EXAMPLES_COPY.previous}
									onClick={() => step(-1)}
								/>
								<IconButton
									Icon={ChevronRight}
									variant="ghost"
									aria-label={IDEA_EXAMPLES_COPY.next}
									onClick={() => step(1)}
								/>
							</>
						)}
					</div>
				</div>
				<div
					data-testid="agent-idea-question"
					className="flex items-start gap-2"
				>
					<Avatar
						name={customer}
						image={contact?.imageUrl ?? null}
						colorSeed={contact?.email ?? customer}
						size="xs"
						className="flex-none"
					/>
					<div className="flex min-w-0 flex-col gap-1.5">
						<span className="truncate text-[13px] font-medium leading-5 text-primary">
							{customer}
						</span>
						<p
							className={cn(
								BUBBLE,
								"bg-gray-100 text-primary dark:bg-gray-800",
							)}
						>
							{item.question}
						</p>
					</div>
				</div>
				{item.agentAnswer ? (
					<div className="flex items-start gap-2">
						<AgentAvatar />
						<div className="flex min-w-0 flex-1 flex-col gap-1.5">
							<span className="truncate text-[13px] font-medium leading-5 text-primary">
								{AI_CHAT_ACTOR_NAME}
							</span>
							<ClampedBubble
								testId="agent-idea-answer"
								className="bg-gray-100 text-primary dark:bg-gray-800"
							>
								{item.agentAnswer}
							</ClampedBubble>
						</div>
					</div>
				) : loading ? (
					<div className="flex items-start gap-2">
						<AgentAvatar />
						<div className="flex min-w-0 flex-1 flex-col gap-1.5">
							<span className="truncate text-[13px] font-medium leading-5 text-primary">
								{AI_CHAT_ACTOR_NAME}
							</span>
							<ReplySkeleton testId="agent-idea-answer-loading" />
						</div>
					</div>
				) : (
					// The agent never replied, so the outcome reads as a status, not a message.
					<p
						data-testid="agent-idea-outcome"
						className="pl-7 text-xs text-tertiary"
					>
						{IDEA_EXAMPLES_COPY.noAnswer(item.outcome)}
					</p>
				)}
				{engineering && exampleAction && (
					<div
						data-testid="agent-idea-example-action"
						className="flex flex-col"
					>
						<span className="pb-1.5 text-xs text-tertiary">
							{IDEA_EXAMPLES_COPY.proposes}
						</span>
						<StripeActionApprovalCard
							actionType={exampleAction.actionType}
							summary={exampleAction.summary}
							preview={exampleActionPreview(exampleAction)}
							status="pending"
							exampleOnly
							disabled
							onApprove={NOOP}
							onReject={NOOP}
						/>
					</div>
				)}
			</section>
		)
	},
)
